import React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import {
  // DocumentDownloadIcon, 
  CheckIcon, 
  LockClosedIcon,
  PlusIcon,
  PlusCircleIcon,
  SearchIcon,
  PencilIcon,
  ExclamationIcon,
} from '@heroicons/react/solid';
import useAnalytics from '../../../../hooks/useAnalytics';
import InfoTooltip from '../../../../components/InfoTooltip';
import { formatAppointmentDateDisplay } from '../../../../utils/appointmentDateHelpers';

const AssessmentModuleCard = ({ 
  title,
  clinician,
  supportingClinicians,
  progress,
  status,
  patientId,
  startPath,
  reviewPath,
  docsAvailable,
  isLocked = false,
  isDisabled = false,
  disabledReason = '',
  circleNumber,
  lockedTooltip = '',
  showAddSupportingButton = false,
  onAddSupportingClick,
  // Accessible label for the purple "+" add-clinician control. ADHD MDT Review
  // uses the default; Autism DD/MDT uses a pathway-specific label because that
  // action also assigns the Observation clinician.
  addSupportingLabel = 'Add MDT review clinician',
  showAssignClinicianButton = false,
  onAssignClinicianClick,
  assignClinicianLabel = 'Assign Clinician',
  // Dual-button mode props (opt-in via previewPath)
  previewPath,
  actionPath,
  actionLabel,
  canStart,
  startDisabledTooltip = 'Only the assigned clinician can start this module',
  // Optional location.state passed to the dual-mode action Link. Used by
  // AutismPatientOverview to trigger auto-open of the appointment checklist
  // dialog on the destination page. Omitted/undefined is a no-op.
  actionState,
  // Lead SSI override (SLaM / 3 Borough) — opt-in props, same pattern as
  // showAddSupportingButton. When the Lead has taken over, overrideInfo
  // drives the "Taken over" annotation and the snapshot link.
  showOverrideButton = false,
  onOverrideClick,
  overrideInfo = null, // { takenOver: bool, originalClinicianName, snapshotPath }
  // Reverse (undo) a Lead SSI takeover — TESTING ONLY, gated to dev/staging by
  // the caller. Rendered in the same slot the "Take over" button would occupy,
  // but shown when a takeover IS currently active (so QA can restore the
  // original clinician's state and re-run the flow on the same patient).
  showReverseButton = false,
  onReverseClick,
  isReversing = false,
  // Per-module appointment date (SLaM / 3 Borough). Hidden entirely when the
  // module is disabled/N/A — a disabled card should not show or accept a date.
  appointmentDate = null, // YYYY-MM-DD calendar string (or null)
  // True when appointmentDate was inferred from firstCompletedAt / lastCompletedAt
  // rather than a human-set appointmentDates entry. Drives the subtle marker.
  appointmentDateInferred = false,
  onEditAppointmentDate = null,
  // Label for the date block. Defaults to "Appointment date", but modules that
  // are not carried out in a patient-facing appointment (e.g. Diagnostic
  // Decision, done in the clinician's own time) pass a plainer "Date".
  appointmentDateLabel = 'Appointment date',
  // Optional advisory shown in the card body (e.g. SLaM: "this PAD may be
  // out of date because the formal Diagnostic Decision disagrees with the
  // in-clinic discussion"). Null/empty hides it entirely.
  warningNote = null,
  // Short heading for the advisory (e.g. "Outcome changed"). Optional.
  warningTitle = null,
  // Visual tone of the advisory. 'alert' (amber) demands attention before the
  // clinician has decided; 'acknowledged' (neutral grey) is the quieter state
  // after they have knowingly chosen to leave a mismatch as is.
  warningTone = 'alert',
  // Optional action node rendered beneath the advisory text (e.g. a
  // "Redo discussion" button + "View original" link for the PAD redo flow).
  warningActions = null,
  // Compact secondary link above the progress footer — used when we only need
  // a quiet archive link (e.g. post-PAD-redo "View original") without the
  // large advisory banner. Explanation lives in a hover tooltip on the link.
  // Shape: { to, label, tooltip, tooltipId? }
  secondaryLink = null,
}) => {
  const isAcknowledgedTone = warningTone === 'acknowledged';
  const { 
    trackAssessmentModuleCardClicked,
    // trackEvent, 
    MODULE_TYPES } = useAnalytics();
  
  // Map the title to MODULE_TYPES constant for analytics
  const getModuleType = () => {
    const titleToModuleType = {
      'Clinical Interview': MODULE_TYPES.CLINICAL_INTERVIEW,
      'Semi-Structured Interview': MODULE_TYPES.SEMI_STRUCTURED_INTERVIEW,
      'Diagnostic Developmental History': MODULE_TYPES.CLINICAL_INTERVIEW,
      // Legacy display title — kept so older cached configs still map correctly.
      'Developmental Diagnostic Interview': MODULE_TYPES.CLINICAL_INTERVIEW,
      'Behavioural Observation': MODULE_TYPES.CLINICAL_INTERVIEW,
      'Diagnostic Decision': MODULE_TYPES.DIAGNOSIS_DECISION,
      'MDT Review': MODULE_TYPES.DIAGNOSIS_DECISION,
      'Diagnostic Decision (MDT Review)': MODULE_TYPES.DIAGNOSIS_DECISION,
      'Post-Assessment Discussion': MODULE_TYPES.POST_ASSESSMENT_DISCUSSION,
      'Feedback Session': MODULE_TYPES.POST_ASSESSMENT_DISCUSSION,
    };
    return titleToModuleType[title] || title.toLowerCase().replace(/\s+/g, '_');
  };
  
  // Handle module card click with analytics
  const handleModuleCardClick = (e) => {
    if (isDisabled || isLocked) return;
    
    const moduleType = getModuleType();
    const action = progress === 0 ? 'started' : progress === 100 ? 'reviewed' : 'continued';
    
    // Track the module card click event
    trackAssessmentModuleCardClicked(
      moduleType,
      patientId,
      {
        action,
        progress,
        supportingClinicians: supportingClinicians?.length || 0
      }
    );
    
    // COMMENTED OUT: Tracking for specific actions
    /*
    // Also track a separate event for the specific action
    trackEvent(`assessment_module_${action}`, {
      moduleType,
      patientId,
      progress,
      clinician
    });
    */
  };

  // Helper function to get circle styling based on progress
  // const getCircleClass = () => {
  //   if (isDisabled) {
  //     return 'bg-gray-50 border-gray-200 text-gray-400';
  //   }
  //   if (progress === 100) {
  //     return 'bg-green-50 border-green-200 text-green-700';
  //   }
  //   if (progress > 0) {
  //     return 'bg-violet-50 border-violet-200 text-violet-700';
  //   }
  //   return 'bg-gray-50 border-gray-200 text-gray-700';
  // };
  
  return (
    <div className={`p-5 bg-white rounded-xl shadow border border-gray-200 flex flex-col h-full relative
      ${isDisabled ? 'cursor-not-allowed' : ''}`}>
      
      {/* Add overlay with reason message for disabled cards */}
      {isDisabled && (
        <div className="absolute inset-0 bg-gray-800 bg-opacity-10 rounded-xl flex items-center justify-center z-10">
          <div className="bg-white p-2  m-4 rounded-lg shadow flex items-center gap-2">
            <LockClosedIcon className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-700 font-semibold">{disabledReason || 'Not applicable'}</span>
          </div>
        </div>
      )}
      
      {/* Title + assigned clinicians — height varies (e.g. MDT cards with two pills). */}
      <div className="flex-1 min-h-0 text-left text-gray-700 text-base font-semibold leading-normal">
        <div className="mb-2">
          <span>{title}</span>
        </div>
        
        {clinician ? (
          <div className="self-stretch mt-2">
            <span className="text-sm font-semibold text-gray-600 mr-1 block mb-1.5">
              {supportingClinicians && supportingClinicians.length > 0 ? 'Assigned Clinicians:' : 'Assigned Clinician:'}
            </span>
            <div className="flex items-center flex-wrap gap-1.5">
              <span
                className="border inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700"
                title={overrideInfo?.takenOver ? 'Lead clinician (editing taken over)' : 'Assigned Clinician'}
              >
                {clinician}
                {overrideInfo?.takenOver && (
                  <span className="ml-1.5 text-xs font-semibold text-amber-700">Editing taken over</span>
                )}
              </span>
              
              {supportingClinicians && supportingClinicians.map((supportingClinician, index) => (
                <span
                  key={index}
                  className="border inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-50 text-gray-600"
                  title="MDT Review Clinician"
                >
                  {supportingClinician}
                </span>
              ))}

              {/* For MDT review orgs: show a "+" button to add a supporting clinician
                  when none are currently assigned. Visible regardless of module
                  lock/disable state since this is an administrative action. */}
              {showAddSupportingButton && onAddSupportingClick &&
                (!supportingClinicians || supportingClinicians.length === 0) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddSupportingClick();
                  }}
                  className="flex-shrink-0 w-7 h-7 rounded-full border border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-600 flex items-center justify-center transition-colors"
                  title={addSupportingLabel}
                  aria-label={addSupportingLabel}
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              )}

              {/* Lead may take over editing the Semi-Structured Interview. */}
              {showOverrideButton && onOverrideClick && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOverrideClick();
                  }}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                    border border-amber-300 bg-amber-50 text-amber-800
                    hover:bg-amber-100 hover:border-amber-400
                    transition-colors duration-200 cursor-pointer"
                  title="Take over Semi-Structured Interview editing from the assigned clinician"
                  aria-label="Take over Semi-Structured Interview editing"
                >
                  Take over
                </button>
              )}

              {/* Testing-only (dev/staging): undo a Lead takeover so the flow
                  can be re-run on the same patient. Never rendered in prod. */}
              {showReverseButton && onReverseClick && (
                <button
                  type="button"
                  disabled={isReversing}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReverseClick();
                  }}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                    border border-rose-300 bg-rose-50 text-rose-800
                    hover:bg-rose-100 hover:border-rose-400
                    transition-colors duration-200
                    ${isReversing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  title="Reverse the Lead takeover and restore the original SSI clinician (testing only)"
                  aria-label="Reverse the Semi-Structured Interview takeover"
                >
                  {isReversing ? 'Reversing…' : 'Reverse (test only)'}
                </button>
              )}
            </div>

            {/* After takeover: show who originally completed SSI + link to snapshot */}
            {overrideInfo?.takenOver && (
              <div className="mt-1.5 text-left">
                <p className="text-xs text-gray-500">
                  Originally completed by {overrideInfo.originalClinicianName || 'SSI clinician'}
                </p>
                {overrideInfo.snapshotPath && (
                  <Link
                    to={overrideInfo.snapshotPath}
                    className="text-xs font-semibold text-violet-700 hover:text-violet-900 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View original answers
                  </Link>
                )}
              </div>
            )}
          </div>
        ) : showAssignClinicianButton && onAssignClinicianClick ? (
          /* When no clinician is assigned to this module but the user can
             assign one, render a placeholder button that keeps the card at
             the same height as sibling cards that already show a clinician
             badge. The label is configurable (e.g. "Assign Lead" for SLaM,
             "Assign Clinician" for TOC). */
          <div className="self-stretch mt-2">
            <span className="text-sm font-semibold text-gray-600 mr-1 block mb-1.5">
              Assigned Clinician:
            </span>
            <div className="flex items-center flex-wrap gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignClinicianClick();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium
                  border border-secondary bg-white text-primary
                  hover:bg-secondary/10 hover:border-secondary
                  transition-colors duration-200 cursor-pointer"
              >
                <PlusCircleIcon className="w-4 h-4 text-secondary" />
                {assignClinicianLabel}
              </button>
            </div>
          </div>
        ) : null}

        {/* Appointment date — only on applicable (non-disabled) modules when
            the org has appointmentDates enabled and a handler is wired. The
            label is configurable so modules done outside a patient-facing
            appointment (e.g. Diagnostic Decision) can read just "Date". */}
        {!isDisabled && onEditAppointmentDate && (
          <div className="self-stretch mt-2">
            <span className="text-sm font-semibold text-gray-600 mr-1 block mb-1.5">
              {appointmentDateLabel}:
            </span>
            <div className="flex items-center flex-wrap gap-1.5">
              {appointmentDate ? (
                <div className="inline-flex items-center gap-1.5 flex-wrap">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onEditAppointmentDate(); }}
                    className="group border inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                      bg-gray-100 text-gray-700 hover:bg-gray-200
                      transition-all duration-200 ease-in-out cursor-pointer"
                    title={
                      appointmentDateInferred
                        ? 'Estimated from module completion — click to confirm or change'
                        : `Click to edit ${appointmentDateLabel.toLowerCase()}`
                    }
                    aria-label={
                      appointmentDateInferred
                        ? `Estimated ${appointmentDateLabel.toLowerCase()} (${formatAppointmentDateDisplay(appointmentDate)}). Click to confirm or change.`
                        : `Edit ${appointmentDateLabel.toLowerCase()} (${formatAppointmentDateDisplay(appointmentDate)})`
                    }
                  >
                    <span className={`whitespace-nowrap ${appointmentDateInferred ? 'italic text-gray-600' : ''}`}>
                      {formatAppointmentDateDisplay(appointmentDate)}
                    </span>
                    {/* Edit icon slides in on hover/focus so the pill grows slightly
                        and signals the date is clickable without always showing clutter. */}
                    <span
                      className="inline-flex items-center overflow-hidden max-w-0 opacity-0
                        group-hover:max-w-[1.25rem] group-hover:opacity-100 group-hover:ml-1.5
                        group-focus-visible:max-w-[1.25rem] group-focus-visible:opacity-100 group-focus-visible:ml-1.5
                        transition-all duration-200 ease-in-out"
                      aria-hidden="true"
                    >
                      <PencilIcon className="w-3.5 h-3.5 text-gray-500 group-hover:text-gray-700 flex-shrink-0" />
                    </span>
                  </button>
                  {appointmentDateInferred && (
                    <InfoTooltip
                      content="Estimated from the date this module was completed. Click the date to confirm or change it."
                      label={`About estimated ${appointmentDateLabel.toLowerCase()}`}
                      side="top"
                      maxWidth="240px"
                    />
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onEditAppointmentDate(); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium
                    border border-secondary bg-white text-primary
                    hover:bg-secondary/10 hover:border-secondary
                    transition-colors duration-200 cursor-pointer"
                >
                  <PlusCircleIcon className="w-4 h-4 text-secondary" />
                  Add Date
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Advisory banner (e.g. SLaM PAD-out-of-date-vs-DD guardrail). Purely
          informational — it never blocks the module's own actions. The
          'acknowledged' tone renders in quieter neutral grey once the clinician
          has knowingly accepted the mismatch, so it stops nagging like an error
          while keeping the redo option available. */}
      {warningNote && (
        <div
          className={`self-stretch mt-2 rounded-md border px-2.5 py-2 text-xs ${
            isAcknowledgedTone
              ? 'border-gray-200 bg-gray-50 text-gray-600'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
          role="note"
        >
          <div className="flex items-start gap-1.5">
            <ExclamationIcon
              className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                isAcknowledgedTone ? 'text-gray-400' : 'text-amber-600'
              }`}
              aria-hidden="true"
            />
            <div>
              {warningTitle && <p className="font-semibold">{warningTitle}</p>}
              <span>{warningNote}</span>
            </div>
          </div>
          {warningActions && (
            <div className="mt-2 flex items-center flex-wrap gap-3 pl-5">{warningActions}</div>
          )}
        </div>
      )}

      {/* Quiet secondary link (e.g. PAD archive after redo). Kept outside the
          advisory banner so a single "View original" does not inflate the card
          with a grey callout box — the explanation is on hover instead. */}
      {secondaryLink?.to && secondaryLink?.label && (
        <div className="self-stretch mt-2 flex items-center">
          <Link
            to={secondaryLink.to}
            data-tooltip-id={secondaryLink.tooltipId || 'assessment-module-secondary-link'}
            className="text-xs font-semibold text-violet-700 hover:text-violet-900 underline"
            onClick={(e) => e.stopPropagation()}
          >
            {secondaryLink.label}
          </Link>
          {secondaryLink.tooltip && (
            <Tooltip
              id={secondaryLink.tooltipId || 'assessment-module-secondary-link'}
              place="top"
              className="z-50 text-xs font-semibold"
              style={{
                maxWidth: '250px',
                whiteSpace: 'normal',
                backgroundColor: 'rgba(34, 34, 34, 1)',
                borderRadius: '15px',
                color: '#fff',
              }}
            >
              {secondaryLink.tooltip}
            </Tooltip>
          )}
        </div>
      )}

      {/* Progress + CTAs sit in a bottom footer so Preview/Start/Locked buttons
          line up across a row even when clinician badges wrap to extra lines. */}
      <div className="flex flex-col gap-1.5 w-full shrink-0 mt-3" data-module-card-footer>
        <div className="self-stretch py-1.5 justify-start items-center gap-3 inline-flex">
          <div className="flex-grow h-2 relative rounded-lg bg-gray-200">
            <div
              className={`absolute top-0 left-0 h-2 rounded-full transition-width duration-300 bg-green-500`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-gray-700 text-sm font-medium leading-tight">
            {progress}%
          </div>
          {progress === 100 && (
            <CheckIcon className="w-5 h-5 text-green-600 inline-block" />
          )}
        </div>
        
        {isDisabled ? (
          <div className="self-stretch px-3.5 py-2.5 bg-gray-100 rounded-lg border border-gray-200 justify-center items-center gap-1 inline-flex">
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-gray-400 text-sm font-semibold leading-tight">{disabledReason || 'Not applicable'}</div>
            </div>
          </div>
        ) : !startPath && !previewPath ? (
          <div className="self-stretch px-3.5 py-2.5 bg-gray-100 rounded-lg border border-gray-200 justify-center items-center gap-1 inline-flex">
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-gray-400 text-sm font-semibold leading-tight">No action</div>
            </div>
          </div>
        ) : status === 'completed' ? (
          <div className="self-stretch px-3.5 py-2.5 bg-gray-100 rounded-lg shadow border border-gray-200 justify-center items-center gap-1 inline-flex">
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-gray-400 text-sm font-semibold leading-tight">Completed</div>
            </div>
          </div>
        ) : isLocked ? (
          <div className="self-stretch px-3.5 py-2.5 bg-white rounded-lg border border-gray-200 justify-center items-center gap-1.5 inline-flex">
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-gray-400 text-sm font-semibold leading-tight">Locked</div>
            </div>
            {lockedTooltip && (
              <InfoTooltip
                content={lockedTooltip}
                label="Why is this locked?"
                side="top"
                maxWidth="280px"
              />
            )}
          </div>
        ) : previewPath ? (
          /* Dual-button mode: Preview (always) + Start/Continue/Review.
             When canStart is false the action button stays visible but disabled,
             with a hover tooltip — non-assigned clinicians can preview only. */
          <div className="self-stretch flex gap-2">
            <Link
              to={previewPath}
              className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition duration-100 justify-center items-center gap-1.5 inline-flex"
              onClick={() => {
                const moduleType = getModuleType();
                trackAssessmentModuleCardClicked(moduleType, patientId, {
                  action: 'previewed',
                  progress,
                  supportingClinicians: supportingClinicians?.length || 0,
                });
              }}
            >
              <SearchIcon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
              <div className="text-gray-700 text-sm font-semibold leading-tight">Preview</div>
            </Link>
            {canStart ? (
              <Link
                to={actionPath}
                state={actionState}
                className={`flex-1 px-3.5 py-2.5 rounded-lg border-2 border-white transition duration-100 justify-center items-center inline-flex ${
                  actionLabel === 'Review'
                    ? 'bg-green-700 hover:bg-green-600'
                    : 'bg-violet-900 hover:bg-violet-800'
                }`}
                onClick={handleModuleCardClick}
              >
                <div className="px-0.5 justify-center items-center flex">
                  <div className="text-white text-sm font-semibold leading-tight">{actionLabel}</div>
                </div>
              </Link>
            ) : (
              <span
                title={startDisabledTooltip || 'Only the assigned clinician can start this module'}
                aria-label={`${actionLabel} — only the assigned clinician can start this module`}
                className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-200 bg-gray-200 text-gray-400 text-sm font-semibold leading-tight text-center cursor-not-allowed"
              >
                {actionLabel}
              </span>
            )}
          </div>
        ) : progress === 0 ? (
          <Link 
            to={startPath} 
            className="self-stretch px-3.5 py-2.5 hover:bg-violet-800 transition duration-100 bg-violet-900 rounded-lg border-2 border-white justify-center items-center gap-1 inline-flex"
            onClick={handleModuleCardClick}
          >
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-white text-sm font-semibold leading-tight">Start</div>
            </div>
          </Link>
        ) : progress > 0 && progress < 100 ? (
          <Link 
            to={startPath} 
            className="self-stretch px-3.5 py-2.5 hover:bg-violet-800 transition duration-100 bg-violet-900 rounded-lg border-2 border-white justify-center items-center gap-1 inline-flex"
            onClick={handleModuleCardClick}
          >
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-white text-sm font-semibold leading-tight">Continue</div>
            </div>
          </Link>
        ) : progress === 100 ? (
          <Link 
            to={reviewPath || startPath} 
            className="self-stretch px-3.5 py-2.5 bg-green-700 rounded-lg border-2 hover:bg-green-600 transition duration-100 border-white justify-center items-center gap-1 inline-flex"
            onClick={handleModuleCardClick}
          >
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-white text-sm font-semibold leading-tight">Review</div>
            </div>
          </Link>
        ) : (
          <div className="self-stretch px-3.5 py-2.5 bg-white rounded-lg border border-gray-200 justify-center items-center gap-1 inline-flex">
            <div className="px-0.5 justify-center items-center flex">
              <div className="text-gray-400 text-sm font-semibold leading-tight">No action</div>
            </div>
          </div>
        )}
        
        {/* {docsAvailable && !isDisabled ? (
          <div className="self-stretch px-6 pt-4 justify-center items-center gap-4 inline-flex">
            <Link 
              to={`/patient/${patientId}/documents`} 
              className="grow shrink basis-0 h-5 justify-center items-center gap-3 flex"
              onClick={() => trackEvent('documents_accessed', { 
                moduleType: getModuleType(),
                patientId 
              })}
            >
              <div className="justify-center items-center gap-1.5 flex">
                <div className="text-blue-600 text-sm font-semibold leading-tight">Docs available</div>
                <DocumentDownloadIcon className="w-5 h-5 text-blue-600" />
              </div>
            </Link>
          </div>
        ) : (
          <div className="self-stretch px-6 pt-4 justify-center items-center gap-4 inline-flex">
            <div className="grow shrink basis-0 h-5 justify-center items-center gap-3 flex">
              <div className="justify-center items-center gap-1.5 flex">
                <div className="text-gray-400 text-sm font-semibold leading-tight">No docs available</div>
                <DocumentDownloadIcon className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default AssessmentModuleCard;